import { buildMarketingPlanFromAssist } from './buildMarketingPlanFromAssist';
import { seedPlanDrawAmountFromFundingPct } from './calc/resolvePlanFunding';
import { enrichAccountingSnapshot } from './enrichAccountingSnapshot';
import {
  applyCohortAnchoredPlanStart,
  getPlanStartAfterCohortSheet,
} from './calc/planStartMonth';

function responseIncludesCohortData(response) {
  if (!response) {
    return false;
  }
  if (response.cohortSheet?.cohorts?.length) {
    return true;
  }
  const cohort = response.cohort;
  return Boolean(
    cohort?.applied ||
    cohort?.normalized?.applied ||
    cohort?.relativeDay?.applied
  );
}

/** Only drop an existing cohort triangle when the assist turn replaces cohort or is marketing-only. */
function shouldClearCohortSheetOnResponse(response) {
  if (!response || responseIncludesCohortData(response)) {
    return false;
  }
  if (response.cohortSheet === null) {
    return true;
  }
  if (response.accountingSnapshot || response.accounting?.applied) {
    return false;
  }
  if (response.message && !response.marketingPlan?.applied) {
    return false;
  }
  if (response.assistSession && !response.marketingPlan?.applied) {
    return false;
  }
  if (response.marketingPlan?.applied) {
    return false;
  }
  return false;
}

function applyAccountingExtractField(next, field, valueKey, sourceKey, sourceLabel) {
  if (field?.value != null) {
    next[valueKey] = field.value;
    next[sourceKey] = sourceLabel;
  }
}

export function applyAssistResponse(prev, response, fileNames = []) {
  const sourceLabel =
    fileNames.length === 1
      ? `AI from ${fileNames[0]}`
      : fileNames.length > 1
        ? 'AI from uploads'
        : 'AI from chat';

  const next = { ...prev };

  if (response?.assistSession) {
    next.assistSession = response.assistSession;
  }
  const enrichedAccountingSnapshot = response?.accountingSnapshot
    ? enrichAccountingSnapshot(response.accountingSnapshot)
    : null;

  if (enrichedAccountingSnapshot) {
    next.accountingSnapshot = enrichedAccountingSnapshot;
  }

  const accounting = enrichedAccountingSnapshot
    ? {
        applied: true,
        gpm: enrichedAccountingSnapshot.gpm,
        opex: enrichedAccountingSnapshot.opex,
        beginCash: enrichedAccountingSnapshot.beginCash,
        totalAssets: enrichedAccountingSnapshot.totalAssets,
        totalLiabilities: enrichedAccountingSnapshot.totalLiabilities,
        safe: enrichedAccountingSnapshot.safe,
        debt: enrichedAccountingSnapshot.debt,
        netBurn: enrichedAccountingSnapshot.netBurn,
        revenueGrowthYoY: enrichedAccountingSnapshot.revenueGrowthYoy,
      }
    : response?.accounting;

  if (accounting?.applied) {
    applyAccountingExtractField(next, accounting.gpm, 'gpm', 'gpmSource', sourceLabel);
    applyAccountingExtractField(next, accounting.opex, 'opex', 'opexSource', sourceLabel);
    applyAccountingExtractField(
      next,
      accounting.beginCash,
      'beginCash',
      'beginCashSource',
      sourceLabel
    );
    applyAccountingExtractField(
      next,
      accounting.totalAssets,
      'totalAssets',
      'totalAssetsSource',
      sourceLabel
    );
    applyAccountingExtractField(
      next,
      accounting.totalLiabilities,
      'totalLiabilities',
      'totalLiabilitiesSource',
      sourceLabel
    );
    applyAccountingExtractField(next, accounting.safe, 'safe', 'safeSource', sourceLabel);
    applyAccountingExtractField(next, accounting.debt, 'debt', 'debtSource', sourceLabel);
    applyAccountingExtractField(
      next,
      accounting.netBurn,
      'netBurn',
      'netBurnSource',
      sourceLabel
    );
    applyAccountingExtractField(
      next,
      accounting.revenueGrowthYoY ?? accounting.revenueGrowthYoy,
      'revenueGrowthYoy',
      'revenueGrowthYoySource',
      sourceLabel
    );
  }

  if (response?.cohort?.applied && response.cohort.roas?.value?.length) {
    next.roas = [...response.cohort.roas.value];
    next.roasSource =
      response.cohortSheet?.wasFilled != null
        ? 'Cohort Excel (3-mo avg)'
        : sourceLabel;
    next.roasMode = 'ai';
  }

  if (response?.cohortSheet) {
    next.cohortSheet = response.cohortSheet;
  } else if (shouldClearCohortSheetOnResponse(response)) {
    next.cohortSheet = null;
  }

  const cohortAnchoredStart = getPlanStartAfterCohortSheet(next.cohortSheet);
  if (cohortAnchoredStart) {
    next.planStartYear = cohortAnchoredStart.year;
    next.planStartMonth = cohortAnchoredStart.month - 1;
    next.firstPlanMonthIsCurrentMonth = false;
  }

  const marketingFromAssist = buildMarketingPlanFromAssist(
    response?.marketingPlan,
    prev,
    { cohortAnchoredStart }
  );
  if (marketingFromAssist) {
    next.marketing = marketingFromAssist.marketing;
    next.fundingPct = marketingFromAssist.fundingPct;
    next.revSharePct = marketingFromAssist.revSharePct;
    next.planStartYear = marketingFromAssist.planStartYear;
    next.planStartMonth = marketingFromAssist.planStartMonth;
    next.firstPlanMonthIsCurrentMonth =
      marketingFromAssist.firstPlanMonthIsCurrentMonth !== false;
    if (next.marketingPlanManualMode) {
      next.planDrawAmount = seedPlanDrawAmountFromFundingPct(next);
    }
    next.marketingPlanSource =
      enrichedAccountingSnapshot && fileNames.length === 0
        ? 'AI from Drive financials'
        : sourceLabel;
  }

  return next;
}

const ACCOUNTING_CLARIFICATION_DEFAULT =
  'I could not read the finance file clearly. Please re-upload a trial balance or P&L with clear revenue, COGS, operating expense, and cash lines, and tell me which rows to use.';

const COHORT_CLARIFICATION_DEFAULT =
  'Could you clarify the cohort file layout? Please re-upload the cohort file and tell me in chat whether period columns are calendar months or relative day buckets.';

function ensureReuploadGuidance(question, fileLabel, defaultQuestion) {
  const q = String(question || '').trim();
  if (!q) {
    return defaultQuestion;
  }
  if (/re-upload|reupload/i.test(q)) {
    return q;
  }
  return `${q} Please re-upload the ${fileLabel} file with your answer so I can process it again.`;
}

export function assistRequiresCohortReupload(response) {
  return false;
}

export function buildAssistChatText(response) {
  if (!response) return '';
  const terminalStages = new Set(['chat', 'complete', 'done', 'menu']);
  if (
    response.stage &&
    !terminalStages.has(response.stage) &&
    response.explanation &&
    !response.marketingPlan?.applied
  ) {
    return response.explanation;
  }
  if (response.marketingPlan?.needsClarification) {
    return (
      response.marketingPlan.clarificationQuestion ||
      response.explanation ||
      'Could you clarify your marketing plan (month count, start month, spend, and % funded)?'
    );
  }
  if (response.accounting?.needsClarification) {
    const summary = String(
      response.documentSummary || response.accountingSnapshot?.documentSummary || ''
    ).trim();
    const question = ensureReuploadGuidance(
      response.accounting.clarificationQuestion || response.explanation,
      'finance',
      ACCOUNTING_CLARIFICATION_DEFAULT
    );
    if (summary) {
      return `**What these files contain:**\n${summary}\n\n${question}`;
    }
    return question;
  }
  if (response.cohort?.needsClarification) {
    if (response.cohort.clarificationKind === 'mapping_confirmation') {
      return (
        response.cohort.clarificationQuestion ||
        response.explanation ||
        'Reply yes to confirm the proposed column mapping.'
      );
    }
    return ensureReuploadGuidance(
      response.cohort.clarificationQuestion || response.explanation,
      'cohort',
      COHORT_CLARIFICATION_DEFAULT
    );
  }
  if (response.message) {
    return response.message;
  }
  let text = response.explanation || 'Analysis complete.';
  const summary = String(
    response.documentSummary || response.accountingSnapshot?.documentSummary || ''
  ).trim();
  if (summary && !text.includes(summary)) {
    text = `**What these files contain:**\n${summary}\n\n${text}`;
  }
  const details = [];
  const acc = response.accounting;
  if (acc?.gpm?.reason) details.push(`GPM: ${acc.gpm.reason}`);
  if (acc?.opex?.reason) details.push(`Opex: ${acc.opex.reason}`);
  if (acc?.beginCash?.reason) details.push(`Cash: ${acc.beginCash.reason}`);
  if (response.cohort?.roas?.reason) {
    details.push(`ROAS: ${response.cohort.roas.reason}`);
  }
  if (response.marketingPlan?.applied && response.marketingPlan.rows?.length) {
    const totalSpend = response.marketingPlan.rows.reduce(
      (sum, row) => sum + (Number(row.marketingSpend) || 0),
      0
    );
    details.push(
      `Marketing plan: ${response.marketingPlan.rows.length} month(s), $${Math.round(totalSpend).toLocaleString('en-US')} total spend`
    );
  }
  if (response.cohortSheet?.wasFilled) {
    details.push('Cohort sheet: Markov-filled missing ROAS cells');
  } else if (response.cohortSheet) {
    details.push('Cohort sheet: complete upload (no fill needed)');
  }
  if (details.length) {
    text += `\n\n${details.join('\n')}`;
  }
  return text;
}

export function formatAssistTokenUsage(usage) {
  if (!usage) return null;
  const { inputTokens = 0, outputTokens = 0, totalTokens = 0 } = usage;
  if (totalTokens <= 0 && inputTokens <= 0 && outputTokens <= 0) {
    return null;
  }
  const fmt = (n) => Number(n || 0).toLocaleString('en-US');
  const total =
    totalTokens > 0 ? totalTokens : inputTokens + outputTokens;
  return `${fmt(inputTokens)} in · ${fmt(outputTokens)} out · ${fmt(total)} total tokens`;
}
